
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

export const ProfileSettings: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const getUser = async () => {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        const currentDisplayName = user.user_metadata?.display_name || 
                                 user.user_metadata?.full_name || 
                                 user.email?.split('@')[0] || '';
        setDisplayName(currentDisplayName);
      }
      setIsLoading(false);
    };

    getUser();
  }, []);

  const handleSaveProfile = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: displayName }
      });

      if (error) throw error;

      toast({
        title: "Profil oppdatert",
        description: "Visningsnavnet ditt har blitt oppdatert",
      });
    } catch (error) {
      toast({
        title: "Feil ved oppdatering",
        description: "Kunne ikke oppdatere profilen din. Prøv igjen senere.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Laster...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email">E-postadresse</Label>
        <Input
          id="email"
          type="email"
          value={user?.email || ''}
          disabled
          className="bg-muted"
        />
        <p className="text-sm text-muted-foreground">
          E-postadressen kan ikke endres
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="displayName">Visningsnavn</Label>
        <Input
          id="displayName"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Ditt visningsnavn"
        />
        <p className="text-sm text-muted-foreground">
          Dette navnet vil vises i applikasjonen
        </p>
      </div>

      <Button 
        onClick={handleSaveProfile} 
        disabled={isSaving}
        className="w-full sm:w-auto"
      >
        {isSaving ? "Lagrer..." : "Lagre endringer"}
      </Button>
    </div>
  );
};
